import React, { useState, useEffect } from 'react';
import { Search, MessageSquare, User, Bot, Trash2, Archive, RefreshCw, Eye, Filter, Loader2, AlertCircle, Check, Square, Download, FileText, CheckSquare } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { BusinessReportManager } from '../../components/reports/BusinessReportManager';

interface Conversation {
  id: string;
  titulo: string;
  usuario_id: string;
  asistente_id: string;
  estado: 'activa' | 'archivada';
  created_at: string;
  updated_at: string;
  openai_thread_id?: string;
  conversation_text?: string;
  file_ids?: string[];
  file_metadata?: any;
  usuario: {
    nombre: string;
    apellidos: string;
    email: string;
  };
  asistente: {
    nombre: string;
    modelo: string;
  };
  mensajes_count?: number;
  ultimo_mensaje?: {
    contenido: string;
    role: string;
    created_at: string;
  };
}

interface User {
  id: string;
  email: string;
  nombre: string;
  apellidos: string;
  grupo_id: string;
}

interface Assistant {
  id: string;
  nombre: string;
  modelo: string;
}

interface Message {
  id: string;
  conversacion_id: string;
  contenido: string;
  role: string;
  created_at: string;
  openai_message_id?: string;
  metadata?: any;
  openai_thread_id?: string;
}

export default function Conversations() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'todas' | 'activas' | 'archivadas'>('todas');
  const [filterUser, setFilterUser] = useState<string>('');
  const [filterAssistant, setFilterAssistant] = useState<string>('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedConversations, setSelectedConversations] = useState<Set<string>>(new Set());
  const [showMessagesModal, setShowMessagesModal] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState<Set<string>>(new Set());
  const [showReportManager, setShowReportManager] = useState(false);
  const [reportContent, setReportContent] = useState('');

  useEffect(() => {
    fetchConversations();
    fetchUsers();
    fetchAssistants();
  }, [filterStatus, filterUser, filterAssistant]);

  // Check if all selectable messages are selected
  const areAllMessagesSelected = () => {
    const selectableMessages = messages.filter(m => m.role !== 'system' && m.role !== 'error');
    return selectableMessages.length > 0 && selectedMessages.size === selectableMessages.length;
  };

  // Toggle selection of all messages
  const toggleAllMessages = () => {
    if (areAllMessagesSelected()) {
      // If all are selected, deselect all
      setSelectedMessages(new Set());
    } else {
      // Otherwise, select all selectable messages
      const selectableMessageIds = messages
        .filter(m => m.role !== 'system' && m.role !== 'error')
        .map(m => m.id);
      setSelectedMessages(new Set(selectableMessageIds));
    }
  };

  // Automatically show report manager when messages are selected
  useEffect(() => {
    if (selectedMessages.size > 0) {
      // Get content from selected messages
      const selectedContent = Array.from(selectedMessages)
        .map(id => {
          const message = messages.find(m => m.id === id);
          return message ? `${message.role === 'user' ? 'Usuario' : 'Asistente'}: ${message.contenido}` : '';
        })
        .filter(Boolean)
        .join('\n\n');
      
      if (selectedContent) {
        setReportContent(selectedContent);
        setShowReportManager(true);
      }
    }
  }, [selectedMessages, messages]);

  const fetchConversations = async () => {
    try {
      setIsLoading(true);
      
      let query = supabase
        .from('conversaciones')
        .select(`
          *,
          usuario:usuarios(nombre, apellidos, email),
          asistente:asistentes(nombre, modelo),
          mensajes_count:mensajes(count),
          ultimo_mensaje:mensajes(contenido, role, created_at)
        `)
        .order('updated_at', { ascending: false });

      if (filterStatus !== 'todas') {
        query = query.eq('estado', filterStatus);
      }
      
      if (filterUser) {
        query = query.eq('usuario_id', filterUser);
      }
      
      if (filterAssistant) {
        query = query.eq('asistente_id', filterAssistant);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform data to handle count and last message
      const transformedData = data?.map(conv => ({
        ...conv,
        mensajes_count: conv.mensajes_count?.[0]?.count || 0,
        ultimo_mensaje: conv.ultimo_mensaje?.[0]
      }));

      setConversations(transformedData || []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setError('Error al cargar las conversaciones');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('usuarios')
        .select('id, nombre, apellidos, email')
        .order('nombre');
        
      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAssistants = async () => {
    try {
      const { data, error } = await supabase
        .from('asistentes')
        .select('id, nombre, modelo')
        .order('nombre');
        
      if (error) throw error;
      setAssistants(data || []);
    } catch (error) {
      console.error('Error fetching assistants:', error);
    }
  };

  const fetchMessages = async (conversationId: string) => {
    try {
      setIsLoadingMessages(true);
      
      const { data, error } = await supabase
        .from('mensajes')
        .select('*')
        .eq('conversacion_id', conversationId)
        .order('created_at');
        
      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError('Error al cargar los mensajes');
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleArchiveConversations = async () => {
    if (selectedConversations.size === 0) {
      setError('Selecciona al menos una conversación');
      return;
    }

    try {
      const conversationsToUpdate = Array.from(selectedConversations);
      const selectedConvsData = conversations.filter(conv => selectedConversations.has(conv.id));
      const newStatus = selectedConvsData[0].estado === 'activa' ? 'archivada' : 'activa';
      
      const { error } = await supabase
        .from('conversaciones')
        .update({ 
          estado: newStatus,
          updated_at: new Date().toISOString()
        })
        .in('id', conversationsToUpdate);

      if (error) throw error;

      setSuccess(`${selectedConversations.size} conversación(es) ${newStatus === 'archivada' ? 'archivada(s)' : 'activada(s)'} correctamente`);
      setSelectedConversations(new Set());
      fetchConversations();
    } catch (error: any) {
      console.error('Error updating conversations:', error);
      setError('Error al actualizar las conversaciones');
    }
  };

  const handleDeleteConversations = async () => {
    if (selectedConversations.size === 0) {
      setError('Selecciona al menos una conversación');
      return;
    }

    if (!confirm(`¿Estás seguro de eliminar ${selectedConversations.size} conversación(es)? Esta acción eliminará todos los mensajes asociados y no se puede deshacer.`)) {
      return;
    }

    try {
      const conversationsToDelete = Array.from(selectedConversations);
      
      const { error } = await supabase
        .from('conversaciones')
        .delete()
        .in('id', conversationsToDelete);

      if (error) throw error;

      setSuccess(`${selectedConversations.size} conversación(es) eliminada(s) correctamente`);
      setSelectedConversations(new Set());
      fetchConversations();
    } catch (error: any) {
      console.error('Error deleting conversations:', error);
      setError('Error al eliminar las conversaciones');
    }
  };

  const handleViewMessages = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation.id);
    setShowMessagesModal(true);
  };

  const toggleConversationSelection = (conversationId: string) => {
    setSelectedConversations(prev => {
      const next = new Set(prev);
      if (next.has(conversationId)) {
        next.delete(conversationId);
      } else {
        next.add(conversationId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedConversations.size === filteredConversations.length) {
      setSelectedConversations(new Set());
    } else {
      setSelectedConversations(new Set(filteredConversations.map(conv => conv.id)));
    }
  };

  const toggleMessageSelection = (messageId: string) => {
    setSelectedMessages(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  const downloadMessageAsText = (message: Message) => {
    try {
      // Create a blob with the message content
      const blob = new Blob([message.contenido], { type: 'text/plain' });
      
      // Create a URL for the blob
      const url = URL.createObjectURL(blob);
      
      // Create a temporary anchor element
      const a = document.createElement('a');
      a.href = url;
      
      // Set the file name - use timestamp to make it unique
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const role = message.role === 'user' ? 'usuario' : 'asistente';
      a.download = `mensaje-${role}-${timestamp}.txt`;
      
      // Append to the document, click, and remove
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Release the URL object
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading message:', error);
      alert('Error al descargar el mensaje');
    }
  };

  const renderMessageContent = (content: string) => {
    if (!content) return null;
    
    // Check if content is too long and truncate if necessary
    const maxLength = 100;
    const truncated = content.length > maxLength 
      ? content.substring(0, maxLength) + '...' 
      : content;
    
    // Handle markdown links
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = truncated.split(new RegExp(`(${markdownLinkRegex.source}|${urlRegex.source})`, 'g'));

    return parts.map((part, index) => {
      if (!part) return null;

      const markdownMatch = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (markdownMatch) {
        const [_, label, url] = markdownMatch;
        return (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            {label}
          </a>
        );
      }

      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            {part}
          </a>
        );
      }

      return <span key={index}>{part}</span>;
    }).filter(Boolean);
  };

  const filteredConversations = conversations.filter(conv => 
    conv.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.usuario.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${conv.usuario.nombre} ${conv.usuario.apellidos}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.asistente.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Gestión de Conversaciones</h1>
        <div className="flex space-x-2">
          <button 
            onClick={handleArchiveConversations}
            disabled={selectedConversations.size === 0}
            className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
          >
            <Archive className="w-5 h-5 mr-2" />
            {selectedConversations.size > 0 && conversations.find(c => selectedConversations.has(c.id))?.estado === 'activa' 
              ? 'Archivar' 
              : 'Activar'} ({selectedConversations.size})
          </button>
          <button 
            onClick={handleDeleteConversations}
            disabled={selectedConversations.size === 0}
            className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-5 h-5 mr-2" />
            Eliminar ({selectedConversations.size})
          </button>
          <button 
            onClick={() => fetchConversations()}
            className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Actualizar
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex flex-wrap items-center mb-4 gap-4">
            <div className="relative flex-1 min-w-[300px]">
              <input
                type="text"
                placeholder="Buscar conversaciones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
            
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <span className="text-gray-700">Filtros:</span>
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as 'todas' | 'activas' | 'archivadas')}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="todas">Todas</option>
              <option value="activas">Activas</option>
              <option value="archivadas">Archivadas</option>
            </select>
            
            <select
              value={filterUser}
              onChange={(e) => setFilterUser(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los usuarios</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.nombre} {user.apellidos}
                </option>
              ))}
            </select>
            
            <select
              value={filterAssistant}
              onChange={(e) => setFilterAssistant(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los asistentes</option>
              {assistants.map(assistant => (
                <option key={assistant.id} value={assistant.id}>
                  {assistant.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedConversations.size === filteredConversations.length && filteredConversations.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Título</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Usuario</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Asistente</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Estado</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Último mensaje</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Actualizado</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Mensajes</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredConversations.map((conv) => (
                  <tr key={conv.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedConversations.has(conv.id)}
                        onChange={() => toggleConversationSelection(conv.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-4 py-3 font-medium">
                      <div className="flex items-center">
                        <MessageSquare className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0" />
                        <span className="truncate max-w-[200px]" title={conv.titulo}>
                          {conv.titulo}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-1 text-gray-500 flex-shrink-0" />
                        <span className="truncate max-w-[150px]" title={`${conv.usuario.nombre} ${conv.usuario.apellidos}`}>
                          {conv.usuario.nombre} {conv.usuario.apellidos}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <Bot className="w-4 h-4 mr-1 text-gray-500 flex-shrink-0" />
                        <span className="truncate max-w-[150px]" title={conv.asistente.nombre}>
                          {conv.asistente.nombre}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        conv.estado === 'activa' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {conv.estado === 'activa' ? 'Activa' : 'Archivada'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {conv.ultimo_mensaje ? (
                        <div className="text-sm text-gray-600 truncate max-w-[200px]" title={conv.ultimo_mensaje.contenido}>
                          <span className="font-medium">{conv.ultimo_mensaje.role === 'user' ? 'Usuario' : 'Asistente'}:</span>{' '}
                          {renderMessageContent(conv.ultimo_mensaje.contenido)}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">Sin mensajes</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(conv.updated_at).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                        {conv.mensajes_count}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleViewMessages(conv)}
                        className="p-1 text-blue-500 hover:bg-blue-100 rounded-full"
                        title="Ver mensajes"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredConversations.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center py-8 text-gray-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No se encontraron conversaciones</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal para ver mensajes */}
      {showMessagesModal && selectedConversation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-4xl h-[90vh] rounded-lg flex flex-col">
            <div className="bg-white shadow-sm px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-800">{selectedConversation.titulo}</h2>
                  <div className="flex items-center mt-1 text-sm text-gray-500">
                    <User className="w-4 h-4 mr-1" />
                    <span className="mr-4">{selectedConversation.usuario.nombre} {selectedConversation.usuario.apellidos}</span>
                    <Bot className="w-4 h-4 mr-1" />
                    <span>{selectedConversation.asistente.nombre}</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowMessagesModal(false);
                    setSelectedMessages(new Set());
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 bg-white shadow-sm border-x border-gray-200">
              {isLoadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Report Generation Button - Only show when messages are selected */}
                  {selectedMessages.size > 0 && (
                    <div className="sticky top-0 z-10 mb-4 bg-blue-50 p-3 rounded-lg flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-700">
                        {selectedMessages.size} {selectedMessages.size === 1 ? 'mensaje seleccionado' : 'mensajes seleccionados'}
                      </span>
                      <button
                        onClick={() => {
                          const selectedContent = Array.from(selectedMessages)
                            .map(id => {
                              const message = messages.find(m => m.id === id);
                              return message ? `${message.role === 'user' ? 'Usuario' : 'Asistente'}: ${message.contenido}` : '';
                            })
                            .filter(Boolean)
                            .join('\n\n');
                          
                          if (selectedContent) {
                            setReportContent(selectedContent);
                            setShowReportManager(true);
                          }
                        }}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors flex items-center"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Generar Informe
                      </button>
                    </div>
                  )}

                  {messages.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No hay mensajes en esta conversación</p>
                    </div>
                  ) : (
                    messages.map((message) => (
                      <div
                        key={message.id}
                        className="flex justify-start"
                      >
                        {message.role === 'system' ? (
                          <div className="bg-gray-100 px-4 py-2 rounded-full text-sm text-gray-600 shadow-sm">
                            {message.contenido}
                          </div>
                        ) : (
                          <div className="flex items-start space-x-2 max-w-[80%] relative group">
                            {/* Selection icon - appears on hover or when selected */}
                            {message.role !== 'error' && (
                              <button
                                onClick={() => toggleAllMessages()}
                                className={`absolute -left-8 top-2 p-1 rounded transition-opacity ${
                                  selectedMessages.size > 0 ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                                } ${
                                  areAllMessagesSelected() ? 'text-blue-500 hover:bg-blue-50' : 'text-gray-400 hover:bg-gray-100'
                                }`}
                                title={areAllMessagesSelected() ? "Deseleccionar todos" : "Seleccionar todos"}
                              >
                                {areAllMessagesSelected() ? (
                                  <CheckSquare className="w-5 h-5" />
                                ) : (
                                  <Square className="w-5 h-5" />
                                )}
                              </button>
                            )}
                            
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${
                                message.role === 'user'
                                  ? 'bg-blue-500'
                                  : message.role === 'error'
                                  ? 'bg-red-500'
                                  : 'bg-gray-200'
                              }`}
                            >
                              {message.role === 'user' ? (
                                <User className="w-5 h-5 text-white" />
                              ) : message.role === 'error' ? (
                                <AlertCircle className="w-5 h-5 text-white" />
                              ) : (
                                <Bot className="w-5 h-5 text-gray-700" />
                              )}
                            </div>
                            
                            <div className="relative">
                              <div
                                className={`p-4 rounded-lg font-mono text-sm whitespace-pre-wrap shadow-sm ${
                                  message.role === 'user'
                                    ? 'bg-blue-500 text-white'
                                    : message.role === 'error'
                                    ? 'bg-red-50 text-red-700 border border-red-100'
                                    : 'bg-gray-50 text-gray-900 border border-gray-100'
                                }`}
                              >
                                {message.contenido}
                              </div>
                              
                              {/* Message actions - appear on hover */}
                              {message.role !== 'error' && (
                                <div className={`absolute right-0 top-0 mt-1 mr-1 flex space-x-1 transition-opacity ${
                                  selectedMessages.size > 0 ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                                }`}>
                                  <button
                                    onClick={() => toggleMessageSelection(message.id)}
                                    className={`p-1 rounded ${
                                      selectedMessages.has(message.id) ? 'bg-blue-100 text-blue-500' : 'bg-gray-100 text-gray-400'
                                    } hover:bg-blue-50 hover:text-blue-500 transition-colors`}
                                    title={selectedMessages.has(message.id) ? "Deseleccionar" : "Seleccionar"}
                                  >
                                    {selectedMessages.has(message.id) ? (
                                      <Check className="w-4 h-4" />
                                    ) : (
                                      <Square className="w-4 h-4" />
                                    )}
                                  </button>
                                  
                                  <button
                                    onClick={() => downloadMessageAsText(message)}
                                    className="p-1 rounded bg-gray-100 text-gray-400 hover:bg-blue-50 hover:text-blue-500 transition-colors"
                                    title="Descargar como texto"
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            
            <div className="bg-white shadow-sm border-t border-gray-200 p-4">
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowMessagesModal(false);
                    setSelectedMessages(new Set());
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Manager Modal */}
      {showReportManager && (
        <BusinessReportManager
          content={reportContent}
          onClose={() => {
            setShowReportManager(false);
            setSelectedMessages(new Set());
          }}
        />
      )}
    </div>
  );
}